import { LitElement, html } from 'lit';
import { StoreController } from '@nanostores/lit';
import {
  $bundleItems,
  $bundleItemsCount,
  $expanded,
  syncTransientBundleItems
} from '@/js/bundle-store.js';

import { bundleBuilderStyles } from './bundle-builder-styles';

export class BundleBuilder extends LitElement {
  static styles = bundleBuilderStyles;

  /**
   * Subscribes to bundle store variables
   *
   * @constructor
   * @returns {html}
   */
  constructor() {
    super();

    this.bundleItemsCount = new StoreController(this, $bundleItemsCount);
    this.expanded = new StoreController(this, $expanded);
    this.render();
  }

  /**
   * Reads the data attributes from the root element and import child elements
   * @returns {null}
   */
  connectedCallback() {
    super.connectedCallback();

    // Sync bundle state on connection to ensure latest state from localStorage
    syncTransientBundleItems();

    this.importChildComponents();
    this.setActiveHeightCustomProperty();
    this.setupPageshowListener();

    this.boxIconUrl = this.dataset.boxIconUrl;
    this.tagIconUrl = this.dataset.tagIconUrl;
    this.placeholderImageUrl = this.dataset.placeholderImageUrl;
    this.iconArrowUpUrl = this.dataset.iconArrowUpUrl;
  }

  /**
   * Cleanup event listeners when component is disconnected
   * @returns {null}
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.pageshowHandler) {
      window.removeEventListener('pageshow', this.pageshowHandler);
    }
  }

  /**
   * Setup pageshow event listener to sync bundle state when page is restored from back-forward cache
   * This ensures the bundle builder shows the latest state when navigating back/forward
   *
   * @returns {null}
   */
  setupPageshowListener() {
    this.pageshowHandler = () => {
      syncTransientBundleItems();
    };
    window.addEventListener('pageshow', this.pageshowHandler);
  }

  /**
   * Import the child components box information, bundle products & bundle builder actions
   *
   * @returns {null}
   */
  importChildComponents() {
    $bundleItems.subscribe(() => {
      if ($bundleItems.value.length > 0) {
        if (!customElements.get('bundle-notifications')) {
          import('./bundle-notifications');
        }
        if (!customElements.get('box-information')) {
          import('./box-information');
        }
        if (!customElements.get('bundle-products')) {
          import('./bundle-products');
        }
        if (!customElements.get('bundle-builder-actions')) {
          import('./bundle-builder-actions');
        }
      }
    });
  }

  /**
   * Adds a resize observer, and sets a css property for the height of the bundle builder when it changes
   */
  setActiveHeightCustomProperty() {
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        const height = entry.contentRect.height;
        document.documentElement.style.setProperty(
          '--bundle-builder-active-height',
          `${height}px`
        );
      }
    });

    const bundleBuilderWrapper = document.querySelector(
      '.bundle-builder-wrapper'
    );
    if (bundleBuilderWrapper) {
      observer.observe(bundleBuilderWrapper);
    }
  }

  /**
   * Render the bundle builder HTML
   *
   * @returns {html}
   */
  render() {
    return html`
      <div
        class="bundle-wrapper ${this.bundleItemsCount.value == 0
          ? 'hidden'
          : 'visible'}"
      >
        <bundle-notifications
          class="bundle-notifications-wrapper"
          data-tag-icon-url="${this.tagIconUrl}"
        ></bundle-notifications>
        <div class="inner-wrapper">
          <box-information
            class="box-information-wrapper"
            data-box-icon-url="${this.boxIconUrl}"
            data-icon-arrow-up-url="${this.iconArrowUpUrl}"
          >
          </box-information>
          <bundle-products
            class="product-grid-outer-wrapper ${this.expanded.value
              ? 'expanded'
              : 'contracted'}"
            data-placeholder-image-url=${this.placeholderImageUrl}
          ></bundle-products>
          <bundle-builder-actions
            class="actions-wrapper ${this.expanded.value
              ? 'expanded'
              : 'contracted'}"
          ></bundle-builder-actions>
        </div>
      </div>
    `;
  }
}

customElements.get('bundle-builder') ||
  customElements.define('bundle-builder', BundleBuilder);
